import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { useAuth } from "@/store/auth";
import { LoginPage } from "@/pages/Login";
import { RegisterPage } from "@/pages/Register";
import { InvitePage } from "@/pages/InvitePage";
import { ForgotPasswordPage } from "@/pages/ForgotPassword";
import { ResetPasswordPage } from "@/pages/ResetPassword";
import { MagicLinkPage } from "@/pages/MagicLink";
import { VerifyEmailPage } from "@/pages/VerifyEmail";
import { AdminDashboard } from "@/pages/AdminDashboard";
import { UserDashboard } from "@/pages/UserDashboard";
import { InstructionsPage } from "@/pages/Instructions";
import { useEffect, useState } from "react";
import axios from "axios";
import { api } from "@/api/axios";
import { Loader2 } from "lucide-react";
import { ToastProvider } from "@/components/ui/toast";

function AuthGuard({
  children,
  adminOnly = false,
}: {
  children: React.ReactNode;
  adminOnly?: boolean;
}) {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(!user);

  useEffect(() => {
    if (user) return;

    let mounted = true;
    let attempts = 0;
    const maxAttempts = 3;

    const tryLoad = () => {
      const controller = new AbortController();
      const hardTimeout = setTimeout(() => controller.abort(), 12000);

      api
        .get("/api/auth/me", { signal: controller.signal })
        .then((res) => {
          if (!mounted) return;
          if (res.data.authenticated && res.data.user) {
            setUser(res.data.user);
          } else {
            setUser(null);
          }
        })
        .catch((err) => {
          clearTimeout(hardTimeout);
          if (!mounted) return;
          if (axios.isCancel?.(err)) {
            setUser(null);
            return;
          }
          if (err.response?.status === 401) {
            setUser(null);
            return;
          }
          console.error("[AuthGuard] /api/auth/me failed:", err);
          attempts += 1;
          if (attempts < maxAttempts) {
            setTimeout(tryLoad, 1000 * attempts);
            return;
          }
          setUser(null);
        })
        .finally(() => {
          clearTimeout(hardTimeout);
          setLoading(false);
        });
    };

    tryLoad();
    return () => {
      mounted = false;
    };
  }, [user, setUser]);

  useEffect(() => {
    if (!loading && !user) {
      navigate("/login", { replace: true });
    }
  }, [loading, user, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (adminOnly && user && !user.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}

function App() {
  return (
    <ToastProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/invite/:token" element={<InvitePage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/magic-link" element={<MagicLinkPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route
            path="/admin"
            element={
              <AuthGuard adminOnly>
                <AdminDashboard />
              </AuthGuard>
            }
          />
          <Route
            path="/dashboard"
            element={
              <AuthGuard>
                <UserDashboard />
              </AuthGuard>
            }
          />
          <Route
            path="/instructions"
            element={
              <AuthGuard>
                <InstructionsPage />
              </AuthGuard>
            }
          />
          <Route path="/" element={<Navigate to="/dashboard" />} />
        </Routes>
      </BrowserRouter>
    </ToastProvider>
  );
}

export default App;
